import React, { useState, useEffect } from 'react';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import './ShopBackOffice.css';
import { Product, ProductImage } from './';
import ProductForm from './ProductForm';

const ShopBackOffice: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState<Partial<Product>>({});
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [authError, setAuthError] = useState<string>('');

const handleLogin = (e: React.FormEvent) => {
  e.preventDefault();

  // Send password to server for verification instead of client-side check
  fetch('/api/shop/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  })
  .then(res => res.json())
  .then(data => {
    if (data.success) {
      setIsAuthenticated(true);
      setAuthError('');
      fetchProducts();
    } else {
      setAuthError('Invalid password');
    }
  })
  .catch(() => {
    setAuthError('Login failed');
  });
};

  // Function to set the main product image
  const setMainImage = async (imageIndex: number) => {
    try {
      // Make a copy of the product images
      const newImages = [...productImages];

      // Reset all images to not be main
      newImages.forEach(img => {
        img.isMain = false;
      });

      // Set the selected one as main
      newImages[imageIndex].isMain = true;

      setProductImages(newImages);
    } catch (error) {
      console.error('Error setting main image:', error);
      alert('Failed to set main image. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  // Fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productService.getAllProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentProduct(null);
    setProductForm({});
    setProductImages([]);
  };

  const handleAddProduct = () => {
    setIsEditing(true);
    setCurrentProduct(null);
    // Initialize form with empty values
    setProductForm({
      titleEn: '',
      titleAr: '',
      price: 0,
      descriptionEn: '',
      descriptionAr: '',
      ageRangeEn: '',
      ageRangeAr: '',
      category: categoryService.getAllCategories('en')[0].id,
      featured: false
    });
    setProductImages([]);
  };

  const handleEditProduct = (product: Product) => {
    setIsEditing(true);
    setCurrentProduct(product);
    // Clone the product to avoid direct mutation
    setProductForm({
      titleEn: product.titleEn,
      titleAr: product.titleAr,
      price: product.price,
      descriptionEn: product.descriptionEn,
      descriptionAr: product.descriptionAr,
      ageRangeEn: product.ageRangeEn,
      ageRangeAr: product.ageRangeAr,
      category: product.category,
      videoUrl: product.videoUrl,
      featured: product.featured
    });
    setProductImages([...product.images]);
  };

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.deleteProduct(productId);
        setProducts(products.filter(p => p.id !== productId));
      } catch (err) {
        console.error('Error deleting product:', err);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setProductForm({ ...productForm, [name]: checked });
    } else if (type === 'number') {
      setProductForm({ ...productForm, [name]: parseFloat(value) });
    } else {
      setProductForm({ ...productForm, [name]: value });
    }
  };

  // Add a new image using URL
  const addImageUrl = (url: string, alt: string) => {
    // Set this image as main if it's the first one
    const isMain = productImages.length === 0;

    // Create a temporary image object with a unique negative ID
    const tempImage: ProductImage = {
      id: -1 * Date.now(), // Negative ID to indicate it's a new image
      url: url,
      alt: alt,
      isMain: isMain
    };

    setProductImages([...productImages, tempImage]);
  };

  const handleUpdateImage = async (index: number, field: keyof ProductImage, value: string) => {
    const updatedImages = [...productImages];
    updatedImages[index] = { ...updatedImages[index], [field]: value };
    setProductImages(updatedImages);
  };

  const handleRemoveImage = async (index: number) => {
    setProductImages(productImages.filter((_, i) => i !== index));

    // If the removed image was main, set the first remaining image as main
    if (productImages[index].isMain && productImages.length > 1) {
      const newImages = [...productImages].filter((_, i) => i !== index);
      if (newImages.length > 0) {
        newImages[0].isMain = true;
        setProductImages(newImages);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productForm.titleEn || !productForm.titleAr || !productForm.category) {
      alert('Please fill in all required fields');
      return;
    }

    if (productImages.length === 0) {
      alert('Please add at least one product image');
      return;
    }

    setLoading(true);

    try {
      let savedProduct: Product;

      if (currentProduct) {
        // Update existing product
        savedProduct = await productService.updateProduct(currentProduct.id, productForm);

        // Handle images for existing product
        // For simplicity, we'll just replace all images
        // In a real application, you might want to handle this more efficiently
        if (savedProduct && productImages.length > 0) {
          // First, remove all existing images
          if (currentProduct.images.length > 0) {
            for (const image of currentProduct.images) {
              try {
                await productService.deleteProductImage(image.id);
              } catch (err) {
                console.error(`Error deleting image ${image.id}:`, err);
                // Continue anyway
              }
            }
          }

          // Then add all new images
          for (const image of productImages) {
            try {
              await productService.addProductImageUrl(
                savedProduct.id,
                image.url,
                image.alt,
                image.isMain
              );
            } catch (err) {
              console.error('Error adding image:', err);
              // Continue anyway
            }
          }
        }
      } else {
        // Create new product
        savedProduct = await productService.createProduct(productForm as Omit<Product, 'id' | 'images'>);

        // Add images to the new product
        if (savedProduct && productImages.length > 0) {
          for (const image of productImages) {
            try {
              await productService.addProductImageUrl(
                savedProduct.id,
                image.url,
                image.alt,
                image.isMain
              );
            } catch (err) {
              console.error('Error adding image:', err);
              // Continue anyway
            }
          }
        }
      }

      // Refresh products list
      await fetchProducts();

      // Reset form
      resetForm();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      resetForm();
    }
  };

  // For first-time load
  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="backoffice-login">
        <h2>Shop Admin Login</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="password">Admin Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter admin password"
            />
          </div>
          {authError && <p className="error-message">{authError}</p>}
          <button type="submit" className="login-button">Login</button>

        </form>
      </div>
    );
  }

  if (loading && !isEditing) {
    return (
      <div className="shop-backoffice">
        <div className="backoffice-header">
          <h2>Shop Administration</h2>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="shop-backoffice">
      <div className="backoffice-header">
        <h2>Shop Administration</h2>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!isEditing ? (
        <div className="product-management">
          <div className="section-header">
            <h3>Product Management</h3>
            <button className="add-product-button" onClick={handleAddProduct}>+ Add New Product</button>
          </div>

          {products.length === 0 ? (
            <div className="no-products">
              <p>No products available. Click "Add New Product" to create one.</p>
            </div>
          ) : (
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Image</th>
                    <th>Title (EN)</th>
                    <th>Title (AR)</th>
                    <th>Price (₪)</th>
                    <th>Category</th>
                    <th>Featured</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id}>
                      <td>{product.id}</td>
                      <td>
                        {product.images.length > 0 && (
                          <img
                            src={product.images.find(img => img.isMain)?.url || product.images[0].url}
                            alt={product.images[0].alt}
                            className="product-thumbnail"
                          />
                        )}
                      </td>
                      <td>{product.titleEn}</td>
                      <td>{product.titleAr}</td>
                      <td>{product.price}</td>
                      <td>{categoryService.getCategoryNameEn(product.category)}</td>
                      <td>{product.featured ? '✓' : '×'}</td>
                      <td className="action-buttons">
                        <button
                          className="edit-button"
                          onClick={() => handleEditProduct(product)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <ProductForm
            productForm={productForm}
            handleFormChange={handleFormChange}
            productImages={productImages}
            handleUpdateImage={handleUpdateImage}
            handleRemoveImage={handleRemoveImage}
            setMainImage={setMainImage}
            addImageUrl={addImageUrl}
          />

          <div className="form-buttons">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="save-button"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : (currentProduct ? 'Update Product' : 'Add Product')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShopBackOffice;